/**
 * Hook for managing evaluation reports with IndexedDB + Nostr sync.
 * Kind 30078 (Parameterized Replaceable Events) keyed by ticker.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { useCurrentUser } from './useCurrentUser';
import { useNostrPublish } from './useNostrPublish';
import {
  getAllEvaluations,
  getEvaluation,
  getEvaluationsByTicker,
  saveEvaluation,
  deleteEvaluation,
} from '@/lib/stockDb';
import type { EvaluationReport } from '@/types/stock';

const EVALUATION_KIND = 30078;
const D_TAG_PREFIX = 'stock-eval-';

// ============================================================
// List all evaluations
// ============================================================

export function useAllEvaluations() {
  return useQuery({
    queryKey: ['evaluations', 'all'],
    queryFn: () => getAllEvaluations(),
  });
}

// ============================================================
// Get evaluations by ticker
// ============================================================

export function useEvaluationsByTicker(ticker: string) {
  return useQuery({
    queryKey: ['evaluations', 'ticker', ticker.toUpperCase()],
    queryFn: () => getEvaluationsByTicker(ticker.toUpperCase()),
    enabled: !!ticker,
  });
}

// ============================================================
// Get single evaluation
// ============================================================

export function useEvaluation(id: string | undefined) {
  return useQuery({
    queryKey: ['evaluations', 'id', id],
    queryFn: () => (id ? getEvaluation(id) : undefined),
    enabled: !!id,
  });
}

// ============================================================
// Save (create or update) evaluation
// ============================================================

export function useSaveEvaluation() {
  const queryClient = useQueryClient();
  const { mutateAsync: publishEvent } = useNostrPublish();
  const { user } = useCurrentUser();

  return useMutation({
    mutationFn: async (report: EvaluationReport) => {
      // Save to IndexedDB
      await saveEvaluation(report);

      // Optionally sync to Nostr if logged in
      if (user) {
        try {
          const content = JSON.stringify(report);
          const dTag = `${D_TAG_PREFIX}${report.ticker.toLowerCase()}-${report.id}`;

          await publishEvent({
            kind: EVALUATION_KIND,
            content,
            tags: [
              ['d', dTag],
              ['t', 'stock-fundamentals'],
              ['ticker', report.ticker],
              ['status', report.overallStatus],
              ['alt', `Stock fundamental analysis for ${report.ticker} (${report.overallStatus})`],
            ],
            created_at: Math.floor(Date.now() / 1000),
          });
        } catch (e) {
          // Nostr sync failure is non-fatal - local save succeeded
          console.warn('Nostr sync failed (non-fatal):', e);
        }
      }

      return report;
    },
    onSuccess: (report) => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      queryClient.setQueryData(['evaluations', 'id', report.id], report);
    },
  });
}

// ============================================================
// Delete evaluation
// ============================================================

export function useDeleteEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await deleteEvaluation(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
    },
  });
}

// ============================================================
// Load evaluations from Nostr (sync from relay)
// ============================================================

export function useSyncFromNostr() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('Not logged in');

      const events = await nostr.query([
        {
          kinds: [EVALUATION_KIND],
          authors: [user.pubkey],
          '#t': ['stock-fundamentals'],
          limit: 200,
        },
      ]);

      let synced = 0;
      for (const event of events) {
        try {
          const report = JSON.parse(event.content) as EvaluationReport;
          if (report.id && report.ticker) {
            await saveEvaluation({ ...report, nostrEventId: event.id, syncedAt: Date.now() });
            synced++;
          }
        } catch {
          // Skip malformed events
        }
      }

      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      return synced;
    },
  });
}
